import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Card, CardContent, Chip, Alert, CircularProgress, IconButton } from '@mui/material';
import {
  Description as DescriptionIcon,
  Upload as UploadIcon,
  CreditCard as CreditCardIcon,
  DriveEta as DriveEtaIcon,
  VerifiedUser as VerifiedUserIcon,
  UploadFile as UploadFileIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useReactiveVar } from '@apollo/client';
import { Button, Icon, TucarTheme, Typography } from '@tucar/pit-stop';

import { getAuthStore } from '../../../../graphql/store';
import GET_COMPLIANCE from '../../../../graphql/querys/compliance/getCompliance';
import ATTACH_DOCUMENT from '../../../../graphql/mutations/compliance/attachDocument';
import { ComplianceData } from '../../../../models/compliance';
import { DocumentUploadUtils } from '../../../../helpers/documentUploadUtils';
import ConfirmationDialog from './components/ConfirmationDialog';
import UploadMethodDialog from './components/UploadMethodDialog';

const DocumentsHub = ({ setMessageError, setOpenError, setCurrentTab }) => {
  const [complianceData, setComplianceData] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingDocumentName, setUploadingDocumentName] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [uploadType, setUploadType] = useState(null);

  const {
    data: { uid },
  } = useReactiveVar(getAuthStore);

  const {
    refetch: refetchCompliance,
    loading: complianceLoading,
    error: complianceError,
  } = useQuery(GET_COMPLIANCE.query, {
    variables: {
      entityId: uid,
      entityType: 'driver',
      country: 'CL',
    },
    ...GET_COMPLIANCE.policies,
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      if (data?.getCompliance) {
        setComplianceData(ComplianceData.fromMap(data.getCompliance));
      }
    },
    onError: (error) => {
      setMessageError(`Error al cargar documentos: ${error.message}`);
      setOpenError(true);
    },
  });

  const [attachDocumentMutation] = useMutation(ATTACH_DOCUMENT.mutation, {
    ...ATTACH_DOCUMENT.policies,
    onCompleted: async (data) => {
      if (data?.attachDocument) {
        try {
          const { data: refetchedData } = await refetchCompliance();
          if (refetchedData?.getCompliance) {
            setComplianceData(ComplianceData.fromMap(refetchedData.getCompliance));
          }
        } catch (error) {
          setMessageError('Error al actualizar los documentos');
          setOpenError(true);
        }
      } else {
        setMessageError('Error al subir el documento');
        setOpenError(true);
      }
      setIsUploading(false);
      setUploadingDocumentName(null);
      setUploadDialogOpen(false);
      setConfirmDialogOpen(false);
      setPendingFile(null);
      setUploadType(null);
    },
    onError: (error) => {
      setMessageError(`Error al subir el documento: ${error.message}`);
      setOpenError(true);
      setIsUploading(false);
      setUploadingDocumentName(null);
      setUploadDialogOpen(false);
      setConfirmDialogOpen(false);
      setPendingFile(null);
      setUploadType(null);
    },
  });

  const executeUpload = async (file) => {
    if (!file || !selectedDocument) return;

    setIsUploading(true);
    setUploadingDocumentName(selectedDocument.name);
    setConfirmDialogOpen(false);

    try {
      const base64File = await DocumentUploadUtils.fileToBase64(file);
      const fileExtension = DocumentUploadUtils.getFileExtension(file.name);

      await attachDocumentMutation({
        variables: {
          input: {
            document: {
              encodedFile: base64File,
              fileName: selectedDocument.name,
              fileFormat: fileExtension,
            },
            entity: 'driver',
            entityId: uid,
          },
        },
      });
    } catch (error) {
      setMessageError(`Error al procesar el archivo: ${error.message}`);
      setOpenError(true);
      setIsUploading(false);
    }
  };

  const getDocumentIcon = (document) => {
    const iconName = document.getDocumentIcon();
    const iconProps = { sx: { fontSize: 24 } };

    switch (iconName) {
      case 'creditCard':
        return <CreditCardIcon {...iconProps} />;
      case 'driveEta':
        return <DriveEtaIcon {...iconProps} />;
      case 'verifiedUser':
        return <VerifiedUserIcon {...iconProps} />;
      case 'description':
        return <DescriptionIcon {...iconProps} />;
      default:
        return <DescriptionIcon {...iconProps} />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
    } catch {
      return dateString;
    }
  };

  const getExpirationColor = (document) => {
    const status = document.documentStatus.toUpperCase();
    if (status === 'EXPIRED') return '#F44336';
    if (status === 'EXPIRING') return '#FF9800';
    return '#9E9E9E';
  };

  const shouldShowExpirationBadge = (document) => {
    const status = document.documentStatus.toUpperCase();
    return status === 'EXPIRED' || status === 'EXPIRING';
  };

  const getExpirationBadgeText = (document) => {
    const status = document.documentStatus.toUpperCase();
    if (status === 'EXPIRED') return 'VENCIDO';
    if (status === 'EXPIRING') return 'POR VENCER';
    return '';
  };

  const handleUploadClick = (document) => {
    if (isUploading && uploadingDocumentName === document.name) {
      return;
    }

    setSelectedDocument(document);
    setUploadDialogOpen(true);
  };

  const handleFileSelect = async (file, isCamera = false) => {
    if (!file || !selectedDocument) return;

    const validation = DocumentUploadUtils.validateFile(file);
    if (!validation.isValid) {
      setMessageError(validation.errorMessage);
      setOpenError(true);
      return;
    }

    setPendingFile(file);
    setUploadType(isCamera ? 'camera' : 'file');
    setUploadDialogOpen(false);
    setConfirmDialogOpen(true);
  };

  const handleCameraUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg, image/png';
    input.capture = 'environment';
    input.onchange = (event) => {
      const file = event.target.files?.[0];
      if (file) {
        handleFileSelect(file, true);
      }
    };
    input.click();
  };

  const handleFilePickerUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/pdf';
    input.onchange = (event) => {
      const file = event.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    };
    input.click();
  };

  const handleUploadMethodSelect = (method) => {
    if (method === 'camera') {
      handleCameraUpload();
    } else if (method === 'file') {
      handleFilePickerUpload();
    }
  };

  const getButtonText = (status, documentName) => {
    if (isUploading && uploadingDocumentName === documentName) {
      return 'Subiendo...';
    }

    switch (status.toUpperCase()) {
      case 'REJECTED':
        return 'Volver a subir';
      case 'ERRORS':
        return 'Corregir documento';
      case 'EXPIRED':
        return 'Renovar documento';
      case 'EXPIRING':
        return 'Renovar documento';
      case 'PENDING':
      default:
        return 'Subir documento';
    }
  };

  if (complianceLoading) {
    return (
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
        }}
      >
        <CircularProgress size={32} />
        <Typography fontSize={14} style={{ marginTop: '16px', color: '#666' }}>
          Cargando documentos...
        </Typography>
      </Box>
    );
  }

  if (complianceError || !complianceData) {
    return (
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
        }}
      >
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar los documentos
        </Alert>
        <Button
          onClick={() => refetchCompliance()}
          startAdornment={<Icon name="refresh" color={TucarTheme.colors.blue500} />}
        >
          Reintentar
        </Button>
      </Box>
    );
  }

  if (!complianceData.documents || complianceData.documents.length === 0) {
    return (
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
        }}
      >
        <DescriptionIcon sx={{ fontSize: 64, color: '#9E9E9E', mb: 2 }} />
        <Typography fontSize={14} style={{ color: '#9E9E9E' }}>
          No hay documentos requeridos
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F5F5F5' }}>
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(180deg, rgb(0, 63, 133) 0%, rgb(0, 87, 184) 100%)',
          color: 'white',
          position: 'relative',
          p: 3,
          textAlign: 'center',
        }}
      >
        {/* Back Button */}
        <IconButton
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            color: 'white',
            zIndex: 1,
          }}
          onClick={() => setCurrentTab('general')}
        >
          <ArrowBackIcon />
        </IconButton>

        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}
        >
          <DescriptionIcon sx={{ fontSize: 40 }} />
        </Box>
        <Box sx={{ mb: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography
            fontSize={24}
            style={{
              fontWeight: '700',
              marginBottom: '8px',
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            Centro de Documentos
          </Typography>
          <Typography
            fontSize={16}
            style={{
              opacity: 0.9,
              fontWeight: '400',
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            Gestiona y revisa el estado de tus documentos
          </Typography>
        </Box>
      </Box>

      <Box sx={{ p: 2 }}>
        {complianceData.documents.map((document) => (
          <Card key={document.name} sx={{ mb: 2, borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    backgroundColor: `${document.statusColor}20`,
                    color: document.statusColor,
                  }}
                >
                  {getDocumentIcon(document)}
                </Box>

                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography fontSize={18} style={{ fontWeight: '600', marginBottom: '4px' }}>
                    {document.displayName}
                  </Typography>

                  <Chip
                    label={document.statusDisplayText}
                    size="small"
                    sx={{
                      backgroundColor: document.statusColor,
                      color: 'white',
                      fontWeight: 500,
                      width: 'max-content',
                    }}
                  />
                </Box>
              </Box>

              {(document.uploadDate || document.expirationDate) && (
                <Box sx={{ mb: 2 }}>
                  {document.uploadDate && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <UploadIcon sx={{ fontSize: 16, color: '#666' }} />
                      <Typography fontSize={14} style={{ color: '#666' }}>
                        Subido: {formatDate(document.uploadDate)}
                      </Typography>
                    </Box>
                  )}

                  {document.expirationDate && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DescriptionIcon
                        sx={{
                          fontSize: 16,
                          color: getExpirationColor(document),
                        }}
                      />
                      <Typography fontSize={14} style={{ color: getExpirationColor(document) }}>
                        Vence: {formatDate(document.expirationDate)}
                      </Typography>
                      {shouldShowExpirationBadge(document) && (
                        <Chip
                          label={getExpirationBadgeText(document)}
                          size="small"
                          sx={{
                            backgroundColor: getExpirationColor(document),
                            color: 'white',
                            fontSize: 10,
                            height: 20,
                          }}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {document.shouldShowUploadButton() && (
                  <Button
                    onClick={() => handleUploadClick(document)}
                    startAdornment={<UploadFileIcon />}
                    disabled={isUploading && uploadingDocumentName === document.name}
                  >
                    {getButtonText(document.documentStatus, document.name)}
                  </Button>
                )}

                {document.path && (
                  <Button
                    onClick={() => window.open(document.path, '_blank')}
                    startAdornment={<Icon name="visibilityOutlined" color="rgb(91, 93, 113)" />}
                  >
                    Ver documento
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      <ConfirmationDialog
        open={confirmDialogOpen}
        onClose={() => {
          setConfirmDialogOpen(false);
          setPendingFile(null);
          setUploadType(null);
        }}
        onConfirm={() => executeUpload(pendingFile)}
        pendingFile={pendingFile}
        selectedDocument={selectedDocument}
        uploadType={uploadType}
        isUploading={isUploading}
      />

      <UploadMethodDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onMethodSelect={handleUploadMethodSelect}
        selectedDocument={selectedDocument}
        isUploading={isUploading}
      />
    </Box>
  );
};

DocumentsHub.propTypes = {
  setMessageError: PropTypes.func.isRequired,
  setOpenError: PropTypes.func.isRequired,
  setCurrentTab: PropTypes.func.isRequired,
};

export default DocumentsHub;
